import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiURL } from '../utilities/URLs'
import { affiliateData, company } from '../data/AffiliateData'
import { useTheme } from '../ThemeContext'
import { refreshAccessToken } from '../utilities/extensions'

import directDeposit from '../assets/documents/directDeposit.pdf'
import w9 from '../assets/documents/w9.pdf'

export default function Resources() {
  const navigate = useNavigate()
  const { isDarkMode } = useTheme()

  const [selectedTab, setSelectedTab] = useState(0)
  const [videos, setVideos] = useState(affiliateData.resources.videos)
  const [links, setLinks] = useState(affiliateData.resources.links)

  const [videoURLs, setVideoURLs] = useState([])
  const [linkURLs, setLinkURLs] = useState([])
  const [openIndex, setOpenIndex] = useState(null)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState('')
  const [uploaded, setUploaded] = useState('')

  useEffect(() => {
    const handleGetResources = async () => {
      try {
        const resourcesResponse = await fetch(`${apiURL}/api/getResources`, {
          headers: {
            'Authorization': affiliateData.accessToken
          }
        })
        const resourcesData = await resourcesResponse.json()

        if (resourcesData.response.error) {
          if (resourcesData.response.error === 'Failed To Authenticate Token.') {
            const refreshedToken = await refreshAccessToken()
            refreshedToken && refreshedToken.length > 0 ? await handleGetResources() : navigate('/affiliate', { state: { expired: true } })
          } else {
            setError(resourcesData.response.error)
            setTimeout(() => {
              setError('')
            }, 2000)
          }
        } else {
          setVideos(resourcesData.response.resources.videos)
          setLinks(resourcesData.response.resources.links)
        }
      } catch (error) {
        console.error('Error fetching resources:', error)
        setError('Error fetching resources. Please try again.')
      }
    }

    handleGetResources()
    document.title = `${company.companyName} | Resources`
  }, [])

  useEffect(() => {
    const fetchURLs = async () => {
      try {
        const videoPromises = videos.flatMap((video) =>
          video.videos.map((link) => handleGenerateLink(link))
        )
        const resolvedVideoURLs = await Promise.all(videoPromises)

        const groupedVideoURLs = []
        let index = 0
        videos.forEach((video) => {
          groupedVideoURLs.push(resolvedVideoURLs.slice(index, index + video.videos.length))
          index += video.videos.length
        })
        setVideoURLs(groupedVideoURLs)

        const linkPromises = links.flatMap((link) => link.links.map((link) => handleGenerateLink(link)))
        const resolvedLinkURLs = await Promise.all(linkPromises)

        const groupedLinkURLs = []
        index = 0
        links.forEach((link) => {
          groupedLinkURLs.push(resolvedLinkURLs.slice(index, index + link.links.length))
          index += link.links.length
        })
        setLinkURLs(groupedLinkURLs)
      } catch (error) {
        console.error('Error fetching resources:', error)
        setError('Error fetching resources. Please try again.')
      }
    }

    fetchURLs()
  }, [videos, links])

  const handleGenerateLink = async (link) => {
    try {
      const response = await fetch(`${apiURL}/api/generates3URL?url=${encodeURIComponent(link)}`, {
        headers: {
          Authorization: affiliateData.accessToken,
        },
      })
      const data = await response.json()
      if (data.response.error) {
        throw new Error(data.response.error)
      }
      return data.response.s3Url
    } catch (error) {
      console.error('Error generating link:', error)
      throw error
    }
  }

  const handleDownload = (file, filename) => {
    const link = document.createElement('a')
    link.href = file
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if(file && file.type === 'application/pdf')
      setUploadedFile(file)
  }

  const handleUploadFile = async () => {
    if(!uploadedFile) return
    setIsUploading(true)
    const form = new FormData()
    form.append('form', uploadedFile)

    const uploadResponse = await fetch(`${apiURL}/api/uploadAccountingForm?affiliateId=${encodeURIComponent(affiliateData.account.accountId)}`, {
      method: 'POST',
      headers: {
        'Authorization': affiliateData.accessToken
      },
      body: form
    })
    const uploadData = await uploadResponse.json()
    setIsUploading(false)
    if(uploadData.response.error) {
      if(uploadData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleUploadFile() : navigate('/affiliate', { state: { expired: true } })
      } else
        console.error(uploadData.response.error)
    } else {
      setUploadedFile(null)
      setUploaded('Uploaded Document Successfully, Thank You!')
      setTimeout(() => {
        setUploaded('')
      }, 5000)
    }
  }

  return (
    <div className='w-full min-h-screen flex flex-col items-center justify-center font-mont p-6'>
      <div className='w-full max-w-6xl flex items-center justify-between'>
        <h1 className={`font-bold text-4xl mt-12 mb-12 text-gray-800 w-full text-center ${isDarkMode && 'text-white'}`}>
          Resources
        </h1>
      </div>
      <div className='w-full max-w-6xl flex flex-col justify-center items-center'>
        <div className='flex justify-center gap-4 mb-4'>
          <button onClick={() => setSelectedTab(0)} className={`px-4 py-2 rounded-lg ${selectedTab === 0 ? 'bg-primaryColor text-white font-bold' : `bg-gray-200 text-gray-800 hover:bg-blue-200 transition duration-300 ${isDarkMode && 'bg-gray-700 text-white'}`}`}>
            Videos
          </button>
          <button onClick={() => setSelectedTab(1)} className={`px-4 py-2 rounded-lg ${selectedTab === 1 ? 'bg-secondaryColor text-white font-bold' : `bg-gray-200 text-gray-800 hover:bg-blue-200 transition duration-300 ${isDarkMode && 'bg-gray-700 text-white'}`}`}>
            Important
          </button>
          {company.companyId === '6243c824-9e29-46da-94c6-96d03d8cbb84' && (
            <button onClick={() => setSelectedTab(2)} className={`px-4 py-2 rounded-lg ${selectedTab === 2 ? 'bg-primaryColor text-white font-bold' : `bg-gray-200 text-gray-800 hover:bg-blue-200 transition duration-300 ${isDarkMode && 'bg-gray-700 text-white'}`}`}>
              Accounting
            </button>
          )}
        </div>
        {selectedTab === 0 && videoURLs.length > 0 ? (
          <div className='w-full'>
            {videos.map((video, videoIndex) => (
              <VideoSection
                key={videoIndex}
                title={video.videoTitle}
                desc={video.videoDesc}
                videoLinks={videoURLs[videoIndex]}
              />
            ))}
          </div>
        ) : selectedTab === 1 && linkURLs.length > 0 ? (
          <div className={`mb-10 p-6 rounded-lg shadow-lg lg:w-3/4 w-full ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className='grid gap-6'>
              {links.map((link, linkIndex) => (
                <LinkSection
                  key={linkIndex}
                  title={link.linkTitle}
                  desc={link.linkDesc}
                  pdfLinks={linkURLs[linkIndex]}
                  index={linkIndex}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={`mb-10 p-8 rounded-lg shadow-xl lg:w-3/4 w-full ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-black'}`}>
            <h2 className='text-2xl font-bold mb-2'>Accounting Documents</h2>
            <p className={`${isDarkMode && 'text-white'} mb-6`}>Please download both forms, and fill out completely. Once filled out, please upload <span className='font-bold'>BOTH</span> forms where 'Upload Form Document' is.</p>
            <div className='mb-8 flex flex-col lg:flex-row lg:space-x-4 space-y-4 lg:space-y-0'>
              <button onClick={() => handleDownload(directDeposit, 'Direct_Deposit.pdf')} className='flex-1 bg-blue-500 text-white px-5 py-3 rounded-lg shadow-md hover:bg-blue-600 transition'>
                Download SETC Pros Affiliate Direct Deposit Authorization Form
              </button>
              <button onClick={() => handleDownload(w9, 'W9_Form.pdf')} className='flex-1 bg-green-500 text-white px-5 py-3 rounded-lg shadow-md hover:bg-green-600 transition'>
                Download SETC Pros Affiliate W-9 Form
              </button>
            </div>
            <div className='mt-6'>
              <label className='block text-lg font-medium mb-3'>Upload Form Document</label>
              <div className='border-2 border-dashed border-gray-400 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-gray-500 transition'>
                <input
                  type='file'
                  accept='application/pdf'
                  className='hidden'
                  id='fileUpload'
                  onChange={handleFileUpload}
                />
                <label htmlFor='fileUpload' className='cursor-pointer text-center'>
                  <span className='text-gray-500'>Drag & Drop or</span>{' '}
                  <span className='text-blue-500 font-semibold hover:underline'>Click to Upload</span>
                </label>
              </div>
              {uploaded && (
                <p className='text-center mt-4'>{uploaded}</p>
              )}
              {uploadedFile && (
                <p className="mt-3 text-sm text-gray-400">Uploaded: {uploadedFile.name}</p>
              )}
            </div>
            {uploadedFile && (
              <button onClick={handleUploadFile} disabled={isUploading} className={`mt-4 w-full px-6 py-3 rounded-lg shadow-md text-white transition ${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor'}`}>
                {isUploading ? 'Uploading...' : 'Upload Form'}
              </button>
            )}
          </div>
        )}
        {error && <p>{error}</p>}
      </div>
    </div>
  )

  function VideoSection({ title, desc, videoLinks }) {
    return (
      <div className={`mb-10 p-6 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <p className={`font-bold text-xl text-gray-700 mb-6 ${isDarkMode && 'text-white'}`}>{title}</p>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-10'>
          {videoLinks.map((newURL, index) => (
            <video
              key={index}
              src={newURL}
              className='rounded-lg'
              allowFullScreen
              controls
            ></video>
          ))}
        </div>
        <p className={`${isDarkMode && 'text-white'}`}>{desc}</p>
      </div>
    )
  }

  function LinkSection({ title, desc, pdfLinks, index }) {
    return (
      <div
        onClick={() => {
          if (/Mobi|Android/i.test(navigator.userAgent)) {
            pdfLinks.forEach((newURL) => {
              window.innerWidth <= 768
                ? (window.location.href = newURL)
                : window.open(newURL, '_blank')
            })
          } else setOpenIndex(openIndex === index ? null : index)
        }}
        key={index}
        className={`rounded-lg p-4 cursor-pointer ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
      >
        <p
          className={`font-bold text-2xl ${openIndex === index && 'mb-2'
            } ${isDarkMode && 'text-white'}`}
        >
          {title}
        </p>
        <p className={`${openIndex === index && 'mb-2'} ${isDarkMode && 'text-white'}`}>{desc}</p>
        {openIndex === index && (
          <div className='pdf-container'>
            {pdfLinks.map((newURL, linkIndex) => (
              <embed
                key={linkIndex}
                src={newURL}
                type='application/pdf'
                width='100%'
                height='750px'
              />
            ))}
          </div>
        )}
      </div>
    )
  }
}